// The Solar System view's own settings panel: one switch per visual layer.
//
// The layer list is DATA. A row carries an id, the face the player reads, and
// the getter and setter that reach whatever draws it, so a layer added later
// is one more row rather than another branch in here. Nothing in this module
// knows what an orbit line or a hex grid is.

const STORE_KEY = 'atlas.viewlayers.v1';

export interface ViewLayerRow {
  /** Stable, and the only thing persistence and the instruments address. */
  readonly id: string;
  /** The face on the switch. */
  readonly label: string;
  readonly get: () => boolean;
  readonly set: (on: boolean) => void;
}

export interface ViewLayersConfig {
  host: HTMLElement;
  rows: () => readonly ViewLayerRow[];
  /** Told the list opened or closed, after the state and the DOM's own
   *  `.open` class are already set - a reader of this can measure what the
   *  new state left for anything sharing the pane. */
  onOpenChange?: (open: boolean) => void;
}

export interface ViewLayersHandle {
  readonly el: HTMLElement;
  /** True while the list is open. Closed is the boot state. */
  readonly open: boolean;
  setOpen(on: boolean): void;
  /** Repaint from the rows' own getters; cheap and memoized on the state string. */
  paint(): void;
  dispose(): void;
}

function readOpen(): boolean {
  try {
    return localStorage.getItem(STORE_KEY) === '1';
  } catch {
    return false;
  }
}

function writeOpen(on: boolean): void {
  try {
    localStorage.setItem(STORE_KEY, on ? '1' : '0');
  } catch { /* private mode */ }
}

export function createViewLayers(config: ViewLayersConfig): ViewLayersHandle {
  const el = document.createElement('div');
  el.className = 'atlas-vl';
  config.host.appendChild(el);

  let open = readOpen();
  let paintedKey = '';

  function stateKey(): string {
    const rows = config.rows();
    return `${open ? 'o' : 'c'}|${rows.map((r) => `${r.id}:${r.get() ? 1 : 0}`).join(',')}`;
  }

  function paint(): void {
    const key = stateKey();
    if (key === paintedKey) return;
    paintedKey = key;
    const rows = config.rows();
    const body = open
      ? `<div class="atlas-vl-body">${rows.map((r) => (
        `<button class="atlas-vl-row${r.get() ? ' on' : ''}" data-vl="${r.id}"`
        + ` aria-pressed="${r.get() ? 'true' : 'false'}">`
        + `<span class="atlas-vl-name">${r.label}</span>`
        + `<span class="atlas-vl-state">${r.get() ? 'ON' : 'OFF'}</span></button>`
      )).join('')}</div>`
      : '';
    // The face is the word alone. The bottom bar shares its line with the body
    // card at one corner and the imagery credit at the other, and a count here
    // is the width that pushes it onto one of them; each row's own switch says
    // ON or OFF in the list this opens.
    el.innerHTML = `<button class="atlas-vl-head" data-vl-head`
      + ` title="Which layers the Solar System view draws"`
      + ` aria-expanded="${open ? 'true' : 'false'}">Layers</button>${body}`;
    el.classList.toggle('open', open);
  }

  function setOpen(next: boolean): void {
    if (open === next) return;
    open = next;
    writeOpen(open);
    paint();
    config.onOpenChange?.(open);
  }

  function onClick(e: MouseEvent): void {
    const t = e.target as HTMLElement | null;
    if (!t) return;
    if (t.closest('[data-vl-head]')) { setOpen(!open); return; }
    const row = t.closest('[data-vl]') as HTMLElement | null;
    if (!row) return;
    const id = row.dataset['vl'];
    if (id === undefined) return;
    const spec = config.rows().find((r) => r.id === id);
    if (!spec) return;
    spec.set(!spec.get());
    paint();
  }

  el.addEventListener('click', onClick);
  paint();

  return {
    el,
    get open(): boolean { return open; },
    setOpen,
    paint,
    dispose(): void {
      el.removeEventListener('click', onClick);
      el.remove();
    },
  };
}
